import api from "./index";
import { EntityTag, IApiUser, IEvent, IGallery, IOrganisation, ISystemSetting, IIndexingStatus, ICollectionStats } from "./types";
import { GeneralStatusEnum } from "../../enums/GeneralStatusEnum";
import { PlanLevelEnum } from "../../enums/PlanLevelEnum";

// Admin API extension for the super admin functionality
const adminApi = api.injectEndpoints({
  endpoints: (build) => ({
    // Get all organisations for super admin
    getAllOrganisations: build.query<IOrganisation[], { jwtToken: string }>({
      query: (params) => ({
        url: `/admin/organisations`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${params.jwtToken}`,
        },
      }),
      providesTags: (result) => [
        ...(result || []).map(organisation => ({
          type: EntityTag.Organisation,
          id: organisation.id,
        })),
        {
          type: EntityTag.Organisation,
          id: 'LIST',
        },
      ],
    }),

    // Get organisation by ID
    getOrganisationById: build.query<IOrganisation, { jwtToken: string, organisationId: number }>({
      query: (params) => ({
        url: `/admin/organisations/${params.organisationId}`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${params.jwtToken}`,
        },
      }),
      providesTags: (result) => result ? [{ type: EntityTag.Organisation, id: result.id }] : [],
    }),

    // Get admin organisation by ID
    getAdminOrganisationById: build.query<IOrganisation, { jwtToken: string, id: number }>({
      query: (params) => ({
        url: `/admin/organisations/${params.id}`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${params.jwtToken}`,
        },
      }),
      providesTags: (result) => result ? [{ type: EntityTag.Organisation, id: result.id }] : [],
    }),

    // Create organisation
    createOrganisation: build.mutation<IOrganisation, {
      jwtToken: string,
      payload: {
        name: string,
        plan: PlanLevelEnum,
        adminEmail: string
      }
    }>({
      query: (params) => ({
        url: `/admin/organisations`,
        method: 'POST',
        body: params.payload,
        headers: {
          Authorization: `Bearer ${params.jwtToken}`,
        },
      }),
      invalidatesTags: [{ type: EntityTag.Organisation, id: 'LIST' }],
    }),

    // Update organisation
    updateOrganisation: build.mutation<IOrganisation, {
      jwtToken: string,
      organisationId: number,
      payload: {
        name: string,
        plan: PlanLevelEnum,
        status?: GeneralStatusEnum
      }
    }>({
      query: (params) => ({
        url: `/admin/organisations/${params.organisationId}`,
        method: 'PUT',
        body: params.payload,
        headers: {
          Authorization: `Bearer ${params.jwtToken}`,
        },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: EntityTag.Organisation, id: arg.organisationId },
        { type: EntityTag.Organisation, id: 'LIST' },
      ],
    }),

    // Delete organisation
    deleteOrganisation: build.mutation<void, { jwtToken: string, organisationId: number }>({
      query: (params) => ({
        url: `/admin/organisations/${params.organisationId}`,
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${params.jwtToken}`,
        },
      }),
      invalidatesTags: [{ type: EntityTag.Organisation, id: 'LIST' }],
    }),

    // Get all system users
    getAllUsers: build.query<IApiUser[], { jwtToken: string }>({
      query: (params) => ({
        url: `/admin/users`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${params.jwtToken}`,
        },
      }),
    }),

    // Get organisation users
    getOrganisationUsers: build.query<IApiUser[], { jwtToken: string, organisationId: number }>({
      query: (params) => ({
        url: `/admin/organisations/${params.organisationId}/users`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${params.jwtToken}`,
        },
      }),
    }),

    // Create user in organisation
    createOrganisationUser: build.mutation<IApiUser, {
      jwtToken: string,
      organisationId: number,
      payload: {
        email: string,
        roles: string,
        status: GeneralStatusEnum
      }
    }>({
      query: (params) => ({
        url: `/admin/organisations/${params.organisationId}/users`,
        method: 'POST',
        body: params.payload,
        headers: {
          Authorization: `Bearer ${params.jwtToken}`,
        },
      }),
    }),

    // Update user
    updateUser: build.mutation<IApiUser, {
      jwtToken: string,
      userId: number,
      payload: {
        email: string,
        roles: string,
        status: GeneralStatusEnum
      }
    }>({
      query: (params) => ({
        url: `/admin/users/${params.userId}`,
        method: 'PUT',
        body: params.payload,
        headers: {
          Authorization: `Bearer ${params.jwtToken}`,
        },
      }),
    }),

    // Delete user
    deleteUser: build.mutation<void, { jwtToken: string, userId: number }>({
      query: (params) => ({
        url: `/admin/users/${params.userId}`,
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${params.jwtToken}`,
        },
      }),
    }),

    // Get all events across organisations
    getAllEvents: build.query<IEvent[], { jwtToken: string }>({
      query: (params) => ({
        url: `/admin/events`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${params.jwtToken}`,
        },
      }),
      providesTags: (result) => [
        ...(result || []).map(event => ({
          type: EntityTag.Event,
          id: event.id,
        })),
        {
          type: EntityTag.Event,
          id: 'LIST',
        },
      ],
    }),

    // Get organisation events
    getAdminOrganisationEvents: build.query<IEvent[], { jwtToken: string, organisationId: number }>({
      query: (params) => ({
        url: `/admin/organisations/${params.organisationId}/events`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${params.jwtToken}`,
        },
      }),
      providesTags: (result) => [
        ...(result || []).map(event => ({
          type: EntityTag.Event,
          id: event.id,
        })),
        {
          type: EntityTag.Event,
          id: 'LIST',
        },
      ],
    }),

    // Get all galleries across organisations
    getEventAllGalleries: build.query<IGallery[], { jwtToken: string, eventId: number }>({
      query: (params) => ({
        url: `/admin/event/${params.eventId}/galleries`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${params.jwtToken}`,
        },
      }),
      providesTags: (result) => [
        ...(result || []).map(gallery => ({
          type: EntityTag.Gallery,
          id: gallery.id,
        })),
        {
          type: EntityTag.Gallery,
          id: 'LIST',
        },
      ],
    }),

    getAllGalleries: build.query<IGallery[], { jwtToken: string }>({
      query: (params) => ({
        url: `/admin/galleries`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${params.jwtToken}`,
        },
      }),
      providesTags: (result) => [
        ...(result || []).map(gallery => ({
          type: EntityTag.Gallery,
          id: gallery.id,
        })),
        {
          type: EntityTag.Gallery,
          id: 'LIST',
        },
      ],
    }),

    // Get gallery details with photo count
    getGalleryDetails: build.query<{ gallery: IGallery, photoCount: number }, { jwtToken: string, galleryId: number }>({
      query: (params) => ({
        url: `/admin/galleries/${params.galleryId}`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${params.jwtToken}`,
        },
      }),
      providesTags: (result) => result ? [{ type: EntityTag.Gallery, id: result.gallery.id }] : [],
    }),

    // Get all system settings
    getSystemSettings: build.query<ISystemSetting[], { jwtToken: string }>({
      query: (params) => ({
        url: `/system-settings`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${params.jwtToken}`,
        },
      }),
      providesTags: [{ type: EntityTag.SystemSettings, id: 'LIST' }],
    }),

    // Update a specific system setting
    updateSystemSetting: build.mutation<ISystemSetting, { 
      jwtToken: string, 
      id: number,
      payload: { value: string } 
    }>({
      query: (params) => ({
        url: `/system-settings/${params.id}`,
        method: 'PATCH',
        body: params.payload,
        headers: {
          Authorization: `Bearer ${params.jwtToken}`,
        },
      }),
      invalidatesTags: [{ type: EntityTag.SystemSettings, id: 'LIST' }],
    }),

    // Get organisation features
    getOrganisationFeatures: build.query<any, { jwtToken: string, organisationId: number }>({
      query: (params) => ({
        url: `/admin/organisations/${params.organisationId}/features`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${params.jwtToken}`,
        },
      }),
    }),

    // Update organisation features
    updateOrganisationFeatures: build.mutation<any, { jwtToken: string, organisationId: number, payload: any }>({
      query: (params) => ({
        url: `/admin/organisations/${params.organisationId}/features`,
        method: 'PUT',
        body: params.payload,
        headers: {
          Authorization: `Bearer ${params.jwtToken}`,
        },
      }),
    }),

    // Get system statistics for dashboard
    getSystemStats: build.query<any, { jwtToken: string }>({
      query: (params) => ({
        url: `/admin/stats`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${params.jwtToken}`,
        },
      }),
    }),

    // Update general system settings (for AdminSettingsPage)
    updateSystemSettings: build.mutation<any, { 
      jwtToken: string, 
      payload: { type: string; settings: any } 
    }>({
      query: (params) => ({
        url: `/admin/settings`,
        method: 'PUT',
        body: params.payload,
        headers: {
          Authorization: `Bearer ${params.jwtToken}`,
        },
      }),
    }),

    // Start event indexing
    startEventIndexing: build.mutation<{ message: string; eventId: number }, { 
      jwtToken: string, 
      eventId: number 
    }>({
      query: (params) => ({
        url: `/galleries/event/${params.eventId}/start-indexing`,
        method: 'POST',
        headers: {
          Authorization: `Bearer ${params.jwtToken}`,
        },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: EntityTag.Event, id: arg.eventId },
      ],
    }),

    // Get event indexing status
    getEventIndexingStatus: build.query<IIndexingStatus, { 
      jwtToken: string, 
      eventId: number 
    }>({
      query: (params) => ({
        url: `/galleries/event/${params.eventId}/indexing-status`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${params.jwtToken}`,
        },
      }),
      providesTags: (result, error, arg) => [
        { type: EntityTag.Event, id: arg.eventId },
      ],
    }),

    // Get event collection stats
    getEventCollectionStats: build.query<ICollectionStats, { 
      jwtToken: string, 
      eventId: number 
    }>({
      query: (params) => ({
        url: `/galleries/event/${params.eventId}/collection-stats`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${params.jwtToken}`,
        },
      }),
      providesTags: (result, error, arg) => [
        { type: EntityTag.Event, id: arg.eventId },
      ],
    }),

    // Delete event collection
    deleteEventCollection: build.mutation<{ message: string; eventId: number }, { 
      jwtToken: string, 
      eventId: number 
    }>({
      query: (params) => ({
        url: `/galleries/event/${params.eventId}/collection`,
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${params.jwtToken}`,
        },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: EntityTag.Event, id: arg.eventId },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAllOrganisationsQuery,
  useGetOrganisationByIdQuery,
  useGetAdminOrganisationByIdQuery,
  useCreateOrganisationMutation,
  useUpdateOrganisationMutation,
  useDeleteOrganisationMutation,
  useGetAllUsersQuery,
  useGetOrganisationUsersQuery,
  useCreateOrganisationUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGetAllEventsQuery,
  useGetAdminOrganisationEventsQuery,
  useGetEventAllGalleriesQuery,
  useGetAllGalleriesQuery,
  useGetGalleryDetailsQuery,
  useGetSystemSettingsQuery,
  useUpdateSystemSettingMutation,
  useGetOrganisationFeaturesQuery,
  useUpdateOrganisationFeaturesMutation,
  useGetSystemStatsQuery,
  useUpdateSystemSettingsMutation,
  useStartEventIndexingMutation,
  useGetEventIndexingStatusQuery,
  useGetEventCollectionStatsQuery,
  useDeleteEventCollectionMutation,
} = adminApi;

export default adminApi;